/* eslint-disable eag/match-named-export */
import {Box, Center, HStack, Icon, Text} from 'platform/foundation';
import {cn} from 'platform/shared';
import {match} from 'ts-pattern';

import {Children, MouseEvent, useEffect, useRef, useState, startTransition} from 'react';
import {
  GroupBase,
  MultiValueProps,
  components as reactSelectComponents,
  SelectComponentsConfig,
} from 'react-select';

import {isNil, isNotNil} from 'ramda';
import {isArray, isFunction, isString} from 'ramda-adjunct';

import {noop, Nullish, suffixTestId, useDeepCompareEffect} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {Checkbox} from '../../Checkbox/Checkbox';
import {IconButton} from '../../IconButton/IconButton';
import {useChoice} from '../hooks/choiceContext';
import {ChoiceAvatarImage} from './ChoiceAvatarImage';
import {
  controlVariants,
  groupHeadingClassName,
  indicatorsContainerClassName,
  menuListClassName,
  multiValueRemoveVariants,
  mutedTextClassName,
  optionContentClassName,
  optionCss,
  valueContainerClassName,
  wrapWithClassName,
} from './components';

export type GetItemImage<ValueType> = (
  item: OptionTypeBase<ValueType>
) => string | null | Promise<string | null>;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export const selectComponents = <ValueType, IsMulti extends boolean>({
  getItemImage,
}: {
  getItemImage: GetItemImage<ValueType>;
}): SelectComponentsConfig<
  OptionTypeBase<ValueType | Nullish>,
  IsMulti,
  GroupBase<OptionTypeBase<ValueType | Nullish>>
> => ({
  LoadingMessage: function LoadingMessage({children, ...props}) {
    const context = useChoice();

    return (
      <reactSelectComponents.LoadingMessage {...props}>
        <span data-testid={suffixTestId('choiceLoadingMessage', context)}>{children}</span>
      </reactSelectComponents.LoadingMessage>
    );
  },
  Control: function Control({children, innerRef, innerProps, isDisabled}) {
    const context = useChoice();
    const {css: _innerCss, ...otherProps} = {css: undefined, ...innerProps};
    return (
      <div
        className={cn(
          controlVariants(),
          context.isInvalid && '!border-[var(--color-severity-danger)]'
        )}
        data-testid={suffixTestId('choiceControl', context)}
        ref={innerRef}
        {...otherProps}
        {...(isDisabled && {disabled: true})}
      >
        {children}
      </div>
    );
  },
  Placeholder: function Placeholder({children, ...props}) {
    const context = useChoice();

    return (
      <reactSelectComponents.Placeholder
        {...props}
        data-testid={suffixTestId('choicePlaceholder', context)}
      >
        <span
          className={cn(mutedTextClassName, props.className)}
          data-testid={suffixTestId('choicePlaceholderInner', context)}
        >
          {children}
        </span>
      </reactSelectComponents.Placeholder>
    );
  },
  NoOptionsMessage: ({children, ...props}) => {
    const context = useChoice();

    return (
      <reactSelectComponents.NoOptionsMessage
        {...props}
        data-testid={suffixTestId('choiceNoOptions', context)}
      >
        <span
          className={cn(mutedTextClassName, props.className)}
          data-testid={suffixTestId('choiceNoOptionsInner', context)}
        >
          {children}
        </span>
      </reactSelectComponents.NoOptionsMessage>
    );
  },
  ValueContainer: function ValueContainer({children, ...props}) {
    const context = useChoice();
    const {imageCache, setImageCache} = context;

    const value = props.selectProps?.value;
    const selectedOption =
      props.isMulti || isArray(value) ? null : (props.selectProps?.value ?? null);

    const optionId = (selectedOption as OptionTypeBase<ValueType>)?.value?.toString() ?? '';
    const cachedImageUrl = imageCache.get(optionId);

    useDeepCompareEffect(() => {
      if (!selectedOption || isNotNil(cachedImageUrl)) {
        return;
      }

      const image = getItemImage(selectedOption as OptionTypeBase<ValueType>);

      match(image)
        .when(isString, (url) => setImageCache(optionId, url))
        .when(isNil, () => setImageCache(optionId, null))
        .otherwise((promiseUrl) =>
          promiseUrl
            .then((url) => setImageCache(optionId, url))
            .catch(() => setImageCache(optionId, null))
        );
      // Intentionally key the deep-compare on the primitive `optionId`, not the whole
      // `selectedOption`. An option's `statusIndicator.tooltip` can hold a React element
      // (e.g. user-absence tooltip); deep-comparing it serializes the element's Fiber graph
      // and freezes the app. `optionId` uniquely identifies the selected option's image.
    }, [optionId, cachedImageUrl, setImageCache]);

    if (props.isMulti) {
      return (
        <reactSelectComponents.ValueContainer
          {...props}
          className={cn(valueContainerClassName, props.className)}
          data-testid={suffixTestId('choiceValueContainer', context)}
        >
          {children}
        </reactSelectComponents.ValueContainer>
      );
    }

    return (
      <HStack spacing={1}>
        <ChoiceAvatarImage
          src={cachedImageUrl}
          option={selectedOption as OptionTypeBase<ValueType>}
        />
        <reactSelectComponents.ValueContainer
          {...props}
          className={cn(valueContainerClassName, props.className)}
          data-testid={suffixTestId('choiceValueContainer', context)}
        >
          {children}
        </reactSelectComponents.ValueContainer>
      </HStack>
    );
  },
  MultiValueContainer: function MultiValueContainer({children, innerProps: _innerProps}) {
    const theme = useComponentsTheme();
    const context = useChoice();

    return (
      <Box
        minHeight={4}
        paddingLeft={1}
        maxWidth="100%"
        overflow="hidden"
        borderRadius="xSmall"
        backgroundColor={theme.components.Choice.backgroundColor}
        data-testid={suffixTestId('choiceMultiValueContainer', context)}
      >
        <HStack align="center" spacing={1}>
          {children}
        </HStack>
      </Box>
    );
  },
  IndicatorsContainer: function IndicatorsContainer({children, ...props}) {
    const context = useChoice();
    const childArray = Children.toArray(children);

    return (
      <reactSelectComponents.IndicatorsContainer
        {...props}
        data-testid={suffixTestId('choiceIndicatorContainer', context)}
        className={cn(indicatorsContainerClassName, props.className)}
      >
        {childArray}
      </reactSelectComponents.IndicatorsContainer>
    );
  },
  IndicatorSeparator: function IndicatorSeparator() {
    return null;
  },
  ClearIndicator: function ClearIndicator({innerProps}) {
    const context = useChoice();

    return (
      <div
        {...innerProps}
        data-testid={suffixTestId('choiceClearIndicatorClickableWrapper', context)}
      >
        <IconButton
          size="small"
          icon="navigation/close"
          tabIndex={-1}
          data-testid={suffixTestId('choiceClearIndicator', context)}
        />
      </div>
    );
  },
  DropdownIndicator: function DropdownIndicator({innerProps, isDisabled}) {
    const context = useChoice();

    return (
      <div
        {...innerProps}
        data-testid={suffixTestId('choiceDropdownIndicatorClickableWrapper', context)}
      >
        <IconButton
          size="small"
          icon="navigation/expand_more"
          isDisabled={isDisabled}
          tabIndex={-1}
          data-testid={suffixTestId('choiceDropdownIndicator', context)}
        />
      </div>
    );
  },
  Menu: function Menu({children, ...props}) {
    const context = useChoice();
    return (
      <reactSelectComponents.Menu
        {...props}
        data-testid={suffixTestId('choiceMenu', context)}
        className={`eag--choice__menu ${props.className}`}
      >
        {children}
      </reactSelectComponents.Menu>
    );
  },
  MenuList: function MenuList({innerRef, children, maxHeight}) {
    const context = useChoice();
    // Prevent multiple scrollIntoView calls
    const isCentered = useRef<boolean>(false);

    const handleRef = (node: HTMLDivElement | null) => {
      // Handle the function-type ref
      if (isFunction(innerRef)) {
        innerRef(node);
      }

      // Handle the object-type ref
      if (innerRef && !isFunction(innerRef) && node) {
        innerRef.current = node;
      }

      // Center the selected option to the middle of the menu
      if (node) {
        const selectedOption = node.querySelector('[data-selected="true"]');
        if (selectedOption && isFunction(selectedOption.scrollIntoView) && !isCentered.current) {
          setTimeout(() => {
            selectedOption.scrollIntoView({block: 'center'});
            isCentered.current = true;
          }, 0);
        }
      }
    };

    return (
      <div
        className={cn(menuListClassName)}
        style={{maxHeight}}
        ref={handleRef}
        data-testid={suffixTestId('choiceMenuList', context)}
      >
        {children}
      </div>
    );
  },
  GroupHeading: function GroupHeading({children}) {
    const context = useChoice();
    return (
      <div
        className={cn(groupHeadingClassName)}
        data-testid={suffixTestId('choiceGroupHeading', context)}
      >
        {children}
      </div>
    );
  },
  Option: function Option({
    innerRef,
    innerProps,
    children,
    isFocused,
    isDisabled,
    isSelected,
    selectProps,
    data,
  }) {
    const context = useChoice();
    const {hasOptionCheckbox, imageCache, setImageCache} = context;
    const {css: _innerCss, ...otherProps} = {css: undefined, ...innerProps};

    const optionId = data.value?.toString() ?? '';
    const cachedImageUrl = imageCache.get(optionId);
    const [isLoaded, setIsLoaded] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);
    const innerValue = selectProps?.value;

    useEffect(
      function lazyLoadImageWhenVisible() {
        if (isLoaded || !data || !elementRef.current || isNotNil(cachedImageUrl)) {
          return;
        }

        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting && !isLoaded) {
              startTransition(() => {
                const image = getItemImage(data as OptionTypeBase<ValueType>);

                match(image)
                  .when(isString, (url) => setImageCache(optionId, url))
                  .when(isNil, () => setImageCache(optionId, null))
                  .otherwise((promiseUrl) =>
                    promiseUrl
                      .then((url) => setImageCache(optionId, url))
                      .catch(() => setImageCache(optionId, null))
                  );

                setIsLoaded(true);
              });
            }
          },
          {threshold: 0.1}
        );

        observer.observe(elementRef.current);

        return () => {
          observer.disconnect();
        };
      },
      [data.value, isLoaded, cachedImageUrl, optionId, setImageCache, innerValue] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
    );

    return (
      <div
        className={optionCss(isFocused, isSelected)}
        ref={innerRef}
        {...otherProps}
        {...(isDisabled && {isDisabled: true})}
        data-testid={suffixTestId('choiceOption', context)}
        aria-disabled={isDisabled}
        data-selected={isSelected ? 'true' : 'false'}
        data-focused={isFocused ? 'true' : 'false'}
      >
        {/* Checkbox native onFocus and onBlur events messing up react-select value selection [T20-22340] */}
        <div className={cn(optionContentClassName)} ref={elementRef}>
          {hasOptionCheckbox ? (
            <HStack spacing={2} align="center">
              <Checkbox
                onChange={noop}
                value={isSelected}
                isDisabled={isDisabled}
                data-testid={suffixTestId('option-checkbox', context)}
              />

              <ChoiceAvatarImage src={cachedImageUrl} option={data as OptionTypeBase<ValueType>} />
              {/* @ts-ignore Children is typed as node by react-select - cant change that */}
              <Text size="small">{children}</Text>
            </HStack>
          ) : (
            <HStack spacing={2}>
              <ChoiceAvatarImage src={cachedImageUrl} option={data as OptionTypeBase<ValueType>} />
              <Text size="small">{children}</Text>
            </HStack>
          )}
        </div>
      </div>
    );
  },
  Input: function Input(props) {
    const context = useChoice();
    return (
      <reactSelectComponents.Input
        data-testid={suffixTestId('choiceInput', context)}
        data-name={context['data-name']}
        {...props}
        className={`${props.className} choice-component-input`}
      />
    );
  },
  MultiValue: <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
    props: MultiValueProps<Option, IsMulti, Group>
  ) => {
    const {children, components, data, innerProps, isFocused, selectProps, removeProps} = props;

    const {Container} = components;

    const context = useChoice();

    return (
      <div className="eag--choice__multi-value">
        <Container data={data} innerProps={innerProps} selectProps={selectProps}>
          <Text
            data-testid={suffixTestId('choiceMultiValueLabel', context)}
            size="xSmall"
            color="white"
            overflowWrap="anywhere"
          >
            {children}
          </Text>
          <Box
            borderColor={isFocused ? 'palettes.blue.40.100' : undefined}
            border={isFocused ? '2px solid' : undefined}
          >
            <div
              {...removeProps}
              onClick={(event: MouseEvent<HTMLDivElement>) => {
                // Prevent event propagation to avoid conflicts with AG Grid event handlers
                event.stopPropagation();
                // Call the original removeProps onClick handler if it exists
                if (removeProps?.onClick) {
                  removeProps.onClick(event);
                }
              }}
              className={cn(multiValueRemoveVariants({isDisabled: false}))}
              data-testid={suffixTestId('choiceMultiValueRemoveContainer', context)}
            >
              <Center height={3} width={4}>
                <Icon
                  size={3}
                  color="general.white"
                  value="navigation/close"
                  data-testid={suffixTestId('choiceMultiValueRemove', context)}
                />
              </Center>
            </div>
          </Box>
        </Container>
      </div>
    );
  },
  SelectContainer: function SelectContainer(props) {
    const context = useChoice();
    return (
      <reactSelectComponents.SelectContainer
        data-testid={suffixTestId('choiceComponentContainer', context)}
        {...props}
        className={props.className}
      />
    );
  },
  SingleValue: wrapWithClassName(reactSelectComponents.SingleValue, 'eag--choice__single-value'),
});
